import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";

const MotionDiv = motion.div;

const Tag = ({ children }) => {
  return (
    <span className="text-[16px] font-normal leading-[100%] text-[#412F14] bg-white border border-black/10 rounded px-[6px] py-[4px] w-fit">
      {children}
    </span>
  );
};

const FaqItem = ({ id, question, answer, isOpen, onToggle }) => {
  return (
    <div className="border-b border-black/10">
      <button
        type="button"
        className={`w-full pt-[24px] ${isOpen ? "pb-0" : "pb-[24px]"} flex items-start justify-between gap-6 text-left`}
        onClick={() => onToggle(id)}
        aria-expanded={isOpen}
      >
        <span className="text-[20px] md:text-[24px] font-semibold leading-[140%] text-[#666666]">
          {question}
        </span>
        <span
          className="flex-shrink-0 text-[24px] font-normal leading-none text-[#666666]"
          aria-hidden="true"
        >
          {isOpen ? "−" : "+"}
        </span>
      </button>

      <MotionDiv
        initial={false}
        animate={{
          height: isOpen ? "auto" : 0,
          opacity: isOpen ? 1 : 0,
        }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <div className="pt-[24px] pb-[24px] text-[20px] md:text-[24px] font-normal leading-[140%] text-[#666666]">
          {answer}
        </div>
      </MotionDiv>
    </div>
  );
};

const CommonQuestions = () => {
  const groups = useMemo(
    () => [
      {
        tag: "Pricing & Plans",
        items: [
          {
            q: "Is knooth free to use?",
            a: "knooth offers a 1 week free trial for new subscribers. After the trial, you can continue with a monthly ($9.99/month) or yearly ($59.99/year) subscription.",
          },
          {
            q: "What plans are available?",
            a: "knooth offers monthly ($9.99/month) and yearly ($59.99/year) subscription plans. Both plans include full access to all features.",
          },
          {
            q: "Can I switch between monthly and yearly plans?",
            a: "Yes. You can switch between monthly and yearly subscriptions at any time through your Apple ID subscription settings.",
          },
          {
            q: "Are updates included?",
            a: "Yes. All plans include ongoing updates, improvements, and new features.",
          },
        ],
      },
      {
        tag: "Licensing & Devices",
        items: [
          {
            q: "Can I use knooth on multiple Macs?",
            a: "Yes. One license allows you to install knooth on any Mac using the same Apple ID you purchased with, in accordance with Apple’s App Store licensing rules.",
          },
          {
            q: "Is knooth intended for individual use?",
            a: "Yes. knooth is currently designed for individual use and personal or professional workflows.",
          },
        ],
      },
      {
        tag: "Privacy & Data",
        items: [
          {
            q: "Does knooth upload or store my data?",
            a: "No. knooth does not upload, store, or process your recordings or assets on any external servers. All files are created and saved locally on your device.",
          },
          {
            q: "Who owns the content created with knooth?",
            a: "You do. All recordings, edits, and exported videos are fully owned by you. knooth does not claim ownership over any content you create.",
          },
          {
            q: "Does knooth collect analytics or tracking data?",
            a: (
              <>
                knooth collects limited, anonymized usage analytics using a privacy-first analytics provider. This data helps us understand how the app is used and improve performance and features. No personal data, identifiers, or content is ever collected.
              </>
            ),
          },
        ],
      },
      {
        tag: "AI in knooth",
        items: [
          {
            q: "Does knooth use AI?",
            a: "Yes. knooth includes optional AI-powered features to help with tasks like generating captions and cleaning up speech disfluencies (such as filler words). AI-generated output may not always be accurate, complete, or error-free",
          },
          {
            q: "Is my data sent to the cloud for AI processing?",
            a: (
              <>
                No.
                <br />
                <br />
                All AI processing in knooth happens entirely on your device.
                <br />
                Your audio, video, and text never leave your device during AI operations.
              </>
            ),
          },
          {
            q: "Does knooth use third-party AI services?",
            a: (
              <>
                No.
                <br />
                <br />
                knooth does not use:
                <br />
                •	Cloud-based AI services
                <br />
                •	External APIs
                <br />
                •	Third-party inference servers
                <br />
                <br />
                Everything runs locally inside the app.
              </>
            ),
          },
          {
            q: "What languages does knooth support?",
            a: (
              <>
                knooth's AI features support 25+ languages, including:
                <br />
                <br />
                Bulgarian, Croatian, Czech, Danish, Dutch, English, Estonian, Finnish, French, German, Greek, Hungarian, Italian, Latvian, Lithuanian, Maltese, Polish, Portuguese, Romanian, Slovak, Slovenian, Spanish, Swedish, Russian, Ukrainian
              </>
            ),
          },
          {
            q: "Does knooth collect or store AI-generated data?",
            a: (
              <>
                No.
                <br />
                <br />
                •	AI-generated captions and edits are stored locally in your project files
                <br />
                •	knooth does not collect, store, or transmit AI inputs or outputs
                <br />
                •	We do not use your content to train any models
              </>
            ),
          },
          {
            q: "Will AI features work offline?",
            a: (
              <>
                Yes.
                <br />
                <br />
                Since all AI processing is on-device, AI features work without an internet connection, subject to your device’s performance capabilities.
              </>
            ),
          },
          {
            q: "Why might AI performance vary between devices?",
            a: (
              <>
                knooth’s AI runs locally on your Mac, so performance depends on your hardware. Performance may vary on older or lower-spec devices.
              </>
            ),
          },
          {
            q: "Does AI affect my privacy?",
            a: (
              <>
                No.
                <br />
                <br />
                knooth’s AI runs entirely on your device.
                <br />
                Your content stays on your Mac and is never uploaded or processed in the cloud.
                <br />
                <br />
                Anonymous usage and diagnostics data may be collected to improve the app, but no
                personal data or content is ever collected.
              </>
            ),
          },
          {
            q: "Is AI used for decision-making or profiling?",
            a: (
              <>
                No.
                <br />
                <br />
                AI in knooth is used solely as a creative and productivity tool.
                <br />
                It does not profile users or make automated decisions about you.
              </>
            ),
          },
          {
            q: "What speech recognition technology does Knooth use?",
            a: (
              <>
                This app uses{" "}
                <a
                  href="https://huggingface.co/nvidia/parakeet-tdt-0.6b-v3"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  NVIDIA Parakeet-TDT 0.6B
                </a>{" "}
                for on-device speech recognition. The model has been converted to
                Core ML format for use on Apple devices. Parakeet-TDT is licensed
                under the{" "}
                <a
                  href="https://creativecommons.org/licenses/by/4.0/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Creative Commons Attribution 4.0 International License (CC-BY-4.0)
                </a>
                . Copyright NVIDIA Corporation.
              </>
            ),
          },
        ],
      },
      {
        tag: "App Store & Payments",
        items: [
          {
            q: "How are subscriptions managed?",
            a: "All payments, subscriptions, renewals, and cancellations are handled through Apple’s App Store. knooth does not process payments directly.",
          },
          {
            q: "Can I cancel my subscription anytime?",
            a: "Yes. Subscriptions can be managed or canceled at any time through your Apple ID subscription settings.",
          },
          {
            q: "Does knooth require an internet connection?",
            a: "No internet connection is required to record, edit, or export videos. An internet connection may be needed for downloading updates or managing subscriptions through the App Store.",
          },
        ],
      },
    ],
    []
  );

  const allIds = useMemo(() => {
    const ids = [];
    groups.forEach((group, groupIndex) => {
      group.items.forEach((_, itemIndex) => {
        ids.push(`g${groupIndex}-i${itemIndex}`);
      });
    });
    return ids;
  }, [groups]);

  const [open, setOpen] = useState(() => {
    const initial = {};
    allIds.forEach((id) => {
      initial[id] = false;
    });
    return initial;
  });

  const toggle = (id) => {
    setOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div id="common-questions" className="pt-[100px]">
      <div className="text-center">
        <h2
          className="text-5xl md:text-6xl lg:text-[72px] font-[900] tracking-[-0.02em] leading-none text-transparent bg-clip-text"
          style={{ backgroundImage: "linear-gradient(180deg, #31220D 57%, #666666 100%)" }}
        >
          Common questions
        </h2>
      </div>

      <div className="mt-[80px] max-w-[1000px] mx-auto">
        {groups.map((group, groupIndex) => (
          <div key={group.tag} className={groupIndex === 0 ? "" : "mt-[60px]"}>
            <Tag>{group.tag}</Tag>
            <div className="mt-[12px]">
              {group.items.map((item, itemIndex) => {
                const id = `g${groupIndex}-i${itemIndex}`;
                return (
                  <FaqItem
                    key={id}
                    id={id}
                    question={item.q}
                    answer={item.a}
                    isOpen={!!open[id]}
                    onToggle={toggle}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export { CommonQuestions };
